import { TableCell, TableFooter, TableRow } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import formatCurrency from '../../../util/formatCurrency';
import toNumber from '../../../util/toNumber';
import { IColumns, ITotalColumn } from '../CasMultiRowTable';

interface ICasMultiRowTotalsRow {
  name: string;
  columns: IColumns[];
  totalColumns: ITotalColumn[];
}

const formatTotal = (totalColumn: ITotalColumn, total: number) =>
  totalColumn.format ? totalColumn.format(total) : formatCurrency(total);

/*
  Footer row for CasMultiRowTable, summing the named columns under their own column.
  CasMultiRowWithTotal only handles a single column and renders below the table, so it can't
  serve a report-style total row.

  A total column names the field to sum, plus rowValue when the column is calculated rather
  than stored - a "display" column has no value in the form data to sum.
*/
export default function CasMultiRowTotalsRow({
  name,
  columns,
  totalColumns,
}: ICasMultiRowTotalsRow) {
  const { control } = useFormContext();

  const rows = useWatch({ control, name });

  const totals = new Map<string, string>(
    totalColumns.map((totalColumn) => {
      const { name: columnName, rowValue } = totalColumn;

      const total = (rows ?? []).reduce(
        (sum: number, row: any) => sum + toNumber(rowValue ? rowValue(row) : row?.[columnName]),
        0
      );

      return [columnName, formatTotal(totalColumn, total)];
    })
  );

  return (
    <TableFooter>
      <TableRow className="table-total-row">
        {columns.map((column, index) => (
          <TableCell key={column.name} align={column.align ?? 'inherit'}>
            {totals.has(column.name) ? (
              <strong>{totals.get(column.name)}</strong>
            ) : (
              index === 0 && <strong>Total</strong>
            )}
          </TableCell>
        ))}
        <TableCell />
      </TableRow>
    </TableFooter>
  );
}
